import { useState } from "react";
import { motion } from "framer-motion";
import { Package, BadgeCheck, CalendarClock, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useShop } from "../context/ShopContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const icons = [Package, BadgeCheck, CalendarClock];

const initialForm = { name: "", company: "", phone: "", quantity: "", note: "" };

const B2BSection = () => {
  const { t, lang, cval } = useShop();
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, {
        name: form.name,
        company: form.company,
        phone: form.phone,
        quantity: Number(form.quantity),
        note: form.note || null,
      });
      toast.success(t.b2b.form.success);
      setForm(initialForm);
    } catch {
      toast.error(t.b2b.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="b2b"
      className="bg-brown text-cream py-24 md:py-36"
      data-testid="b2b-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-xs tracking-[0.3em] uppercase font-semibold text-olive-pale mb-4">
            {t.b2b.chapter} — {t.b2b.overline}
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
            {cval(`b2b_title_${lang}`, t.b2b.title)}
          </h2>
          <p className="mt-6 max-w-md text-base font-light leading-relaxed text-cream/70">
            {cval(`b2b_sub_${lang}`, t.b2b.sub)}
          </p>

          <div className="mt-14 flex flex-col gap-10">
            {t.b2b.items.map((item, i) => {
              const Icon = icons[i];
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, delay: i * 0.15 }}
                  className="flex gap-6 items-start"
                  data-testid={`b2b-advantage-${i}`}
                >
                  <span className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-full border border-olive-pale/40 text-olive-pale">
                    <Icon size={22} strokeWidth={1.5} />
                  </span>
                  <div>
                    <h3 className="font-serif text-xl sm:text-2xl font-medium">{item.title}</h3>
                    <p className="mt-2 text-sm md:text-base font-light leading-relaxed text-cream/60">
                      {item.desc}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="bg-cream text-brown p-8 md:p-12 lg:-mt-6"
        >
          <h3 className="font-serif text-2xl sm:text-3xl font-medium" data-testid="b2b-form-title">
            {t.b2b.form.title}
          </h3>
          <p className="mt-3 text-sm font-light text-brown-light">{t.b2b.form.sub}</p>

          <form onSubmit={handleSubmit} className="mt-10 flex flex-col gap-8" data-testid="b2b-quote-form">
            <div>
              <label htmlFor="b2b-name" className="text-[11px] tracking-[0.25em] uppercase text-brown-muted">
                {t.b2b.form.name}
              </label>
              <Input
                id="b2b-name"
                required
                minLength={2}
                value={form.name}
                onChange={set("name")}
                className="editorial-input mt-1"
                data-testid="b2b-name-input"
              />
            </div>
            <div>
              <label htmlFor="b2b-company" className="text-[11px] tracking-[0.25em] uppercase text-brown-muted">
                {t.b2b.form.company}
              </label>
              <Input
                id="b2b-company"
                required
                minLength={2}
                value={form.company}
                onChange={set("company")}
                className="editorial-input mt-1"
                data-testid="b2b-company-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <label htmlFor="b2b-phone" className="text-[11px] tracking-[0.25em] uppercase text-brown-muted">
                  {t.b2b.form.phone}
                </label>
                <Input
                  id="b2b-phone"
                  required
                  type="tel"
                  minLength={7}
                  value={form.phone}
                  onChange={set("phone")}
                  className="editorial-input mt-1"
                  data-testid="b2b-phone-input"
                />
              </div>
              <div>
                <label htmlFor="b2b-quantity" className="text-[11px] tracking-[0.25em] uppercase text-brown-muted">
                  {t.b2b.form.quantity}
                </label>
                <Input
                  id="b2b-quantity"
                  required
                  type="number"
                  min={1}
                  value={form.quantity}
                  onChange={set("quantity")}
                  className="editorial-input mt-1"
                  data-testid="b2b-quantity-input"
                />
              </div>
            </div>
            <div>
              <label htmlFor="b2b-note" className="text-[11px] tracking-[0.25em] uppercase text-brown-muted">
                {t.b2b.form.note}
              </label>
              <Textarea
                id="b2b-note"
                rows={3}
                value={form.note}
                onChange={set("note")}
                className="editorial-input mt-1 resize-none"
                data-testid="b2b-note-input"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="mt-2 inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-brown text-cream text-sm font-medium tracking-wide hover:bg-olive-dark disabled:opacity-60 transition-colors duration-300"
              data-testid="b2b-submit-button"
            >
              {sending && <Loader2 size={16} className="animate-spin" />}
              {sending ? t.b2b.form.sending : t.b2b.form.submit}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default B2BSection;
